import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Empty, Rows } from "@/components/ui-helpers";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const KIND = { stage_change: "Stage", note: "Note / outcome", call: "Call", message: "Message", task_done: "Task", login: "Login", email: "Email" };

function Heatmap({ data }) {
  const max = Math.max(1, ...data.flat());
  return (
    <div className="panel p-4 overflow-x-auto" data-testid="activity-heatmap">
      <div className="text-sm font-medium mb-3">When they work <span className="text-[11px] text-neutral-500 font-normal">day × hour, IST</span></div>
      <div className="inline-grid gap-[3px] min-w-[640px]" style={{ gridTemplateColumns: "36px repeat(24, minmax(18px, 1fr))" }}>
        <span />{Array.from({ length: 24 }, (_, h) => <span key={h} className="text-[9px] text-neutral-600 text-center">{h % 3 === 0 ? h : ""}</span>)}
        {data.map((row, d) => [
          <span key={`l${d}`} className="text-[10px] text-neutral-500 self-center">{DAYS[d]}</span>,
          ...row.map((n, h) => (
            <div key={`${d}-${h}`} title={`${DAYS[d]} ${h}:00 · ${n} actions`} data-testid={`heat-${d}-${h}`} className="h-[18px] rounded-[3px]"
              style={{ background: n ? `rgba(52, 211, 153, ${0.15 + 0.85 * (n / max)})` : "#ffffff0d" }} />
          )),
        ])}
      </div>
    </div>
  );
}

function Daily({ data }) {
  return (
    <div className="panel p-4" data-testid="activity-daily-chart">
      <div className="text-sm font-medium mb-2">Actions per day</div>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 10, fill: "#737373" }} axisLine={false} tickLine={false} minTickGap={16} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#737373" }} axisLine={false} tickLine={false} width={28} />
            <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 12 }} cursor={{ fill: "#ffffff0a" }} />
            <Bar dataKey="n" name="Actions" fill="#34d399" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Timeline({ rows }) {
  if (!rows.length) return <Empty label="No activity in this range" />;
  const groups = rows.reduce((g, r) => { const d = new Date(r.at).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", timeZone: "Asia/Kolkata" }); (g[d] = g[d] || []).push(r); return g; }, {});
  return (
    <div className="panel p-4 space-y-4" data-testid="activity-timeline">
      {Object.entries(groups).map(([d, items]) => (
        <div key={d}>
          <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">{d} · {items.length}</div>
          <div className="space-y-1">{items.map((r, i) => (
            <div key={i} className="flex items-start gap-3 text-xs">
              <span className="font-mono text-neutral-500 w-11 shrink-0">{new Date(r.at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" })}</span>
              <span className="pill text-[10px] shrink-0">{KIND[r.kind] || (r.kind || "").replace(/_/g, " ")}</span>
              <span className="flex-1 break-words text-neutral-300">{r.text}{r.lead_name && <> · <Link className="underline text-neutral-400" to={`/crm?lead=${r.lead_id}`}>{r.lead_name}</Link></>}</span>
            </div>))}</div>
        </div>
      ))}
    </div>
  );
}

export default function TeamActivity() {
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState("vedant.zoho@thickandthin.media");
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/crm/users").then((r) => setUsers(r.data.rows || r.data || [])); }, []);
  useEffect(() => { setD(null); api.get("/team/activity", { params: { user, days } }).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [user, days]);
  return (
    <div data-testid="team-activity-page">
      <PageHeader title="Team activity" sub="Visible only to Khush and Prakhar" right={
        <div className="flex gap-2">
          <select className="px-2 py-1.5 rounded border text-sm" value={user} onChange={(e) => setUser(e.target.value)} data-testid="activity-user-select">
            {users.map((u) => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
          </select>
          <select className="px-2 py-1.5 rounded border text-sm" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="activity-range-select">
            {[7, 14, 30, 90].map((n) => <option key={n} value={n}>Last {n} days</option>)}
          </select>
        </div>} />
      <div className="p-4 md:p-6 space-y-4">
        {!d ? <Rows n={4} /> : d.error ? <Empty label="You don't have access to team activity" /> : <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="activity-stats">
            {[["Actions", d.total], ["Active days", `${d.active_days}/${d.days}`], ["Outcomes & notes", d.by_kind.note || 0], ["Stage moves", d.by_kind.stage_change || 0]].map(([l, v]) => (
              <div key={l} className="panel p-3"><div className="text-[10px] uppercase tracking-wider text-neutral-500">{l}</div><div className="text-2xl font-semibold tabular-nums">{v}</div></div>
            ))}
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4"><Heatmap data={d.heatmap} /><Daily data={d.daily} /></div>
          <Timeline rows={d.rows} />
        </>}
      </div>
    </div>
  );
}
